<template>
    <div data-cy="expand_html">
        <template v-if="isExpendable">
            <div
                ref="expandHtmlRef"
                class="expand-html__content"
                :style="contentStyle"
                data-test-id="expand-html-content"
                v-html="html"
            />
            <span
                class="flex mt-4 text-right font-bold cursor-pointer justify-end items-center"
                data-test-id="expand-html-button"
                @click.prevent="toggle"
            >
                {{ buttonText }}
                <template v-if="isArrowsVisible">
                    <ShcUiIcon v-if="isExpanded" name="expand_less" />
                    <ShcUiIcon v-else name="expand_more" />
                </template>
            </span>
        </template>
        <template v-else>
            <div class="expand-html__content" data-test-id="expand-html-content" v-html="html" />
        </template>
    </div>
</template>

<script lang="ts" setup>
    import { EXPAND_HTML_EVENT } from './expandHtml'
    import type { Props } from './props'

    const props = withDefaults(defineProps<Props>(), {
        isExpandedOnStart: false,
        isArrowsVisible: false,
    })

    const emit = defineEmits<{
        (e: typeof EXPAND_HTML_EVENT.toggle, value: boolean): void
    }>()

    const { $i18n } = useNuxtApp()

    const isExpendable = ref(true)
    const isExpanded = ref(props.isExpandedOnStart)
    const lineHeight = ref(0)
    const expandHtmlRef = ref<HTMLElement | null>(null)
    const contentHeight = ref(0)

    onMounted(() => {
        if (expandHtmlRef.value) {
            const computedStyle = getComputedStyle(expandHtmlRef.value)

            lineHeight.value = Number(computedStyle.lineHeight.replace('px', ''))
            contentHeight.value = expandHtmlRef.value.scrollHeight

            isExpendable.value = contentHeight.value > props.max * lineHeight.value
        }
    })

    const contentStyle = ref({
        display: '-webkit-box',
        overflow: 'hidden',
        'text-overflow': 'ellipsis',
        '-webkit-box-orient': 'vertical',
        '-webkit-line-clamp': props.isExpandedOnStart ? 'auto' : props.max.toString(),
    })

    function toggle(): void {
        isExpanded.value = !isExpanded.value

        contentStyle.value['-webkit-line-clamp'] = isExpanded.value ? 'initial' : props.max.toString()

        emit(EXPAND_HTML_EVENT.toggle, isExpanded.value)
    }

    const buttonText = computed((): string =>
        isExpanded.value ? $i18n.t('Bbk.expandHtml.collapse') : $i18n.t('Bbk.expandHtml.expand'),
    )
</script>

<style scoped>
    .expand-html__content :deep(ul) {
        @apply mt-4 ml-8 list-disc;
    }

    .expand-html__content :deep(ol) {
        @apply mt-4 ml-8 list-decimal;
    }

    .expand-html__content :deep(p) {
        @apply mt-4;
    }
</style>
